import React, { useCallback } from 'react';

import { Group, Paper, Skeleton, Text, Title, createStyles } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import BackButton from 'components/BackButton';
import DownloadCSV from 'components/DownloadCSV';
import HelpButton from 'components/HelpButton';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useGym from 'hooks/gym/useGym';
import useArm from 'hooks/reports/tbb-metrics/useArm';
import useRevenuePerMember from 'hooks/reports/tbb-metrics/useRevenuePerMember';
import { useNavigate } from 'react-router-dom';
import { RevenuePerMemberReport } from 'types';

const useStyles = createStyles(theme => ({
  header: {
    width: '100%',
    background: theme.colors.gray[0],
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    display: 'grid',
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    gridTemplateColumns: 'min-content min-content auto max-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"backButton title summary monthPicker download"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'min-content auto auto max-content min-content',
      gridTemplateAreas: `
        'backButton title title title download'
        'summary summary summary monthPicker monthPicker'
        `,
    },
    [theme.fn.smallerThan('sm')]: {
      padding: theme.spacing.sm,
      gridTemplateColumns: 'min-content auto auto max-content',
      gridTemplateAreas: `
        'backButton title title title'
        'summary summary summary monthPicker'
        `,
    },
  },
  backButton: {
    gridArea: 'backButton',
  },
  title: {
    gridArea: 'title',
  },
  summary: {
    gridArea: 'summary',
    marginLeft: theme.spacing.lg,
  },
  monthPicker: {
    gridArea: 'monthPicker',
  },
  download: {
    gridArea: 'download',
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },
}));

const dataToRow = (d: RevenuePerMemberReport): { [key: string]: any } => ({
  'Full Name': d.fullName,
  'Current Status': d.athleteStatus,
  Email: d.email,
  Phone: d.phone,
  Revenue: (d.revenue / 100).toFixed(2),
});

const ArmReportPageHeader = ({
  type,
  lastMonth,
  month,
  setMonth,
  orderingValue,
}: {
  type: 'total' | 'paying';
  lastMonth: Date;
  month: Date | null;
  setMonth: React.Dispatch<React.SetStateAction<Date | null>>;
  orderingValue: string;
}): JSX.Element => {
  const { classes, theme } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const navigate = useNavigate();

  const { session } = useAuth();
  const { formatCurrency } = useLocalization();

  const { data: gymData } = useGym(session?.gym?.gymId);

  const printDate = dayjs(month).format('MMMM');
  const printType = type[0].toUpperCase() + type.slice(1);

  const { data, isLoading, isError, isSuccess } = useArm({
    month: dayjs(month).format('YYYY-MM'),
    type: type,
  });

  const handleBackClick = useCallback((): void => {
    navigate(-1);
  }, [navigate]);

  const filename = `revenue_per_${type}_member_${dayjs(month).format('YYYY-MM')}`;
  const csvOptions = {
    filename,
    fieldSeparator: ',',
    quoteStrings: '',
    decimalSeparator: '.',
    showLabels: true,
    title: filename,
    useBom: true,
    useKeysAsHeaders: true,
  };

  const getARMValue = (): JSX.Element => {
    if (isLoading) {
      return <Skeleton height='2rem' width='5rem' />;
    }
    if (isError) {
      return (
        <Title order={isDesktop ? 1 : 3} color='red'>
          Error
        </Title>
      );
    }
    if (isSuccess && data) {
      return (
        <Title order={isDesktop ? 1 : 3} color='gray'>
          {formatCurrency(data.data.arm / 100)}
        </Title>
      );
    }
    return <></>;
  };

  const getSummaryText = (): JSX.Element => {
    if (isLoading) {
      return <Skeleton height='2rem' width='5rem' className={classes.summary} />;
    }
    if (isError) {
      return <Text className={classes.summary}>Error</Text>;
    }
    if (isSuccess && data) {
      return (
        <Text className={classes.summary}>
          {`Total Revenue in ${printDate} ${formatCurrency(data.data.revenue / 100)} / ${
            data.data.members
          } ${printType} Members`}
        </Text>
      );
    }
    return <></>;
  };

  return (
    <Paper className={classes.header}>
      <BackButton handleOnClick={handleBackClick} />
      <Group noWrap spacing='xl' className={classes.title} data-cy='ArmReportPageHeader'>
        <Title order={isDesktop ? 1 : 3} sx={{ whiteSpace: 'nowrap' }}>{`ARM ${
          type[0].toUpperCase() + type.slice(1)
        }`}</Title>
        {getARMValue()}
        <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'}>
          <Text size='sm'>
            Average revenue per member over the selected month. This is calculated by the total revenue over
            the month divided by the number of members at the end of the month.
          </Text>
        </HelpButton>
      </Group>

      {getSummaryText()}

      <MonthPickerInput
        className={classes.monthPicker}
        value={month}
        onChange={setMonth}
        valueFormat='MMMM YYYY'
        type='default'
        minDate={new Date(gymData?.data.createdAt || '')}
        maxDate={lastMonth}
        allowDeselect={false}
        required
      />

      <DownloadCSV
        useList={useRevenuePerMember}
        dataToRow={dataToRow}
        hookOptions={{ month: dayjs(month).format('YYYY-MM'), type: type, ordering: orderingValue }}
        csvOptions={csvOptions}
        className={classes.download}
        reportType={`arm-${type}`}
        startDate={month ? dayjs(month).startOf('month').format('YYYY-MM-DD') : null}
        endDate={month ? dayjs(month).endOf('month').format('YYYY-MM-DD') : null}
      />
    </Paper>
  );
};

export default ArmReportPageHeader;
